import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import BankDetails from './sections/BankDetails/BankDetails';
import { ReactComponent as Lightbulb } from '../../../assets/icons/v3/lightbulb.svg';
import BdsCheckbox from '../../../shared/components/v3/BdsCheckbox/BdsCheckbox';
import BdsInput from '../../../shared/components/v3/BdsInput/BdsInput';
import BdsLinearProgressIndicator from '../../../shared/components/v3/BdsLinearProgressIndicator/BdsLinearProgressIndicator';
import BdsButton from '../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { HOME_ROUTE } from '../../../shared/constants/auth';
import { TOAST_ALERT_BALANCE_TIMESTAMP } from '../../../shared/constants/localStorage';
import { CLICK_ACTION, CONFIRM_RECHARGE_BALANCE_RF, CONFIRM_TRANSFER_RF } from '../../../shared/constants/tagManager';
import { DEFAULT_TOAST_DATA, GENERIC_ERROR_TOAST } from '../../../shared/constants/toast';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { IToast } from '../../../shared/interfaces/toast';
import Company from '../../../shared/services/api/companies/companies';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { FLEXIBLE_REMUNERATION_TRANSLATIONS } from '../../../shared/translations/flexibleRemuneration';

import './BalanceCharge.scss';

const BalanceCharge = () => {
  const { companyData } = useContext(CompanyContext);
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [toast, setToast] = useState<IToast>(DEFAULT_TOAST_DATA);
  const navigate = useNavigate();
  const buttonText =
    currentStep === 1
      ? FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.continueButton
      : FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.endButton;
  const heroTitle =
    currentStep === 1
      ? FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.title
      : FLEXIBLE_REMUNERATION_TRANSLATIONS.transferBalance.title;
  const { t } = useTranslation();
  const progress = currentStep === 1 ? 50 : 100;
  const isAmountInvalid = !amount || Number(amount) <= 0 || isNaN(Number(amount));
  const isButtonDisabled = currentStep === 1 && isAmountInvalid;

  const onBackAction = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      return;
    }
    window.history.back();
  };

  const validateAmount = (value: string | null) => {
    const numberValue = Number(value);
    if (numberValue <= 0 || isNaN(numberValue)) {
      return t(FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.errorMoreThanZero);
    }
    return null;
  };

  const handleAmountChange = (value: string) => {
    setAmount(value);
  };

  const sendConfirmRechargeBalanceEvent = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_RECHARGE_BALANCE_RF, { proforma_rf: isChecked });
  };

  const setAlertBalanceTimestamp = (): void => {
    const now = new Date().getTime;
    localStorage.setItem(TOAST_ALERT_BALANCE_TIMESTAMP, now.toString());
  };

  const handleNextStep = async () => {
    if (currentStep === 1 && isAmountInvalid) return;
    if (!isChecked && currentStep === 1) {
      sendConfirmRechargeBalanceEvent();
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 2) {
      TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_TRANSFER_RF);
      await callNotifyAddFunds();
      navigate(HOME_ROUTE, { state: { showBalanceToast: true } });
    } else {
      sendConfirmRechargeBalanceEvent();
      await callNotifyAddFunds();
      navigate(HOME_ROUTE, { state: { showBalanceModal: true } });
    }
  };

  const callNotifyAddFunds = async () => {
    try {
      setLoading(true);
      await Company.notifyAddFunds(companyData.id, Number(amount), 'EUR', isChecked);
      setAlertBalanceTimestamp();
    } catch {
      setToast(GENERIC_ERROR_TOAST);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const renderOneStepContent = () => (
    <div className="flex flex-col gap-13">
      <div className="balance-amount-input">
        <BdsInput
          initValue={amount}
          labelText={t(FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.inputLabel)}
          hintText={'1000 €'}
          onChanged={handleAmountChange}
          validator={validateAmount}
        />
      </div>
      <div className="balance-checkbox flex flex-row items-center gap-2">
        <BdsCheckbox name="" value="" selected={isChecked} onChange={() => setIsChecked(!isChecked)} />
        <span className="balance-checkbox-label">
          {t(FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.description)}
        </span>
      </div>
    </div>
  );

  const renderSecondStepContent = () => (
    <div className="flex flex-col gap-10">
      <div className="transfer-balance-title">{t(FLEXIBLE_REMUNERATION_TRANSLATIONS.transferBalance.transferData)}</div>
      <div className="flex flex-row gap-10">
        <BankDetails />
        <div className="transfer-balance-info flex flex-col gap-2">
          <div className="transfer-balance-pictogram">
            <Lightbulb className="transfer-balance-icon" />
          </div>
          <div className="transfer-balance-info-title">
            {t(FLEXIBLE_REMUNERATION_TRANSLATIONS.transferBalance.infoTitle)}
          </div>
          <div className="transfer-balance-info-description">
            {t(FLEXIBLE_REMUNERATION_TRANSLATIONS.transferBalance.infoDescription)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full min-h-screen" data-experience="new">
      <SecondaryHero
        title={t(FLEXIBLE_REMUNERATION_TRANSLATIONS.balanceCharge.main)}
        description={t(heroTitle)}
        backAction={onBackAction}
        color={'surface-container'}
        bottomContent={<BdsLinearProgressIndicator variant="subtle" size="s" progress={progress} />}
      />
      <div className="flex flex-col p-8 gap-11">
        <div className="balance-charge-container w-full flex py-8 px-12">
          {currentStep === 1 ? renderOneStepContent() : renderSecondStepContent()}
        </div>
        <div className="flex justify-end">
          <BdsButton
            variant="primary"
            size="l"
            action={handleNextStep}
            disabled={isButtonDisabled || loading}
            loading={loading}
          >
            {t(buttonText)}
          </BdsButton>
        </div>
      </div>
      <Toast
        title={t(toast.title)}
        description={t(toast.description)}
        icon={toast.icon}
        open={showErrorToast}
        button={true}
        handleClose={() => setShowErrorToast(false)}
      />
    </div>
  );
};

export default BalanceCharge;
